import { saveAs } from 'file-saver';
import { Observable, Subscription } from 'rxjs';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { tap } from 'rxjs/operators';
import { ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { NotifyService, PayrollService } from '@app/services';
import { PayrollDigest } from '@models/payroll/payroll-digest.model';
import { PayrollDigestService } from '@reporting/services';

@Component({
    templateUrl: './payroll-digest.view.html',
    styleUrls: ['./payroll-digest.style.scss'],
})
export class PayrollDigestView implements OnInit, OnDestroy {
    isLoading = true;

    payrollDigest$: Observable<PayrollDigest[]>;
    showArchived = false;
    isLoadingMore = false;
    private scrollSubscription: Subscription = new Subscription();

    constructor(
        private payrollService: PayrollService,
        private payrollDigestService: PayrollDigestService,
        private notify: NotifyService,
        private element: ElementRef,
        private scrollDispatcher: ScrollDispatcher,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.payrollDigest$ = this.payrollDigestService.payrollDigest$.pipe(
            tap({
                error: (err) => this.notify.error(err.message || 'reporting.reports.payrollDigest.unableToFetchItems'),
            })
        );
    }

    ngOnInit(): void {
        this.getPayrollDigest();

        this.scrollSubscription = this.scrollDispatcher.ancestorScrolled(this.element).subscribe((scrollable) => {
            if (!scrollable) return;

            const pixelsNearBottom = 100;
            const offset = scrollable.measureScrollOffset('bottom');

            if (offset <= pixelsNearBottom) {
                this.loadMore();
            }
        });
    }

    ngOnDestroy(): void {
        this.scrollSubscription.unsubscribe();
    }

    onFilterChange(event: any): void {
        this.getPayrollDigest();
    }

    toggleArchived(): void {
        this.showArchived = !this.showArchived;
        this.getPayrollDigest();
    }

    archive(id: number): void {
        this.payrollService
            .archivePayrollDigestItem(id)
            // Once the rest of the controller is migrated to v2, I'll
            // move the HTTP request into the PayrollDigestService as well.
            .then(() => this.payrollDigestService.archivePayrollDigestItem(id));
    }

    unarchive(id: number): void {
        this.payrollService
            .unarchivePayrollDigestItem(id)
            // Once the rest of the controller is migrated to v2, I'll
            // move the HTTP request into the PayrollDigestService as well.
            .then(() => this.payrollDigestService.unarchivePayrollDigestItem(id));
    }

    onExport(): void {
        this.isLoading = true;

        this.payrollService.exportPayrollDigest(this.showArchived).subscribe((digest) => {
            saveAs(digest, 'payrolldigest.xlsx');
            this.isLoading = false;
        });
    }

    private async getPayrollDigest() {
        this.isLoading = true;

        await this.payrollDigestService.loadInitial(this.showArchived);

        this.isLoading = false;
    }

    private loadMore() {
        if (this.isLoadingMore || !this.payrollDigestService.hasMore()) return;

        this.isLoadingMore = true;
        this.changeDetectorRef.detectChanges();
        this.payrollDigestService.loadMore().then(() => {
            this.isLoadingMore = false;
        });
    }
}
